/**
 * Address Autocomplete for Order Forms
 * Uses Google Maps API for address suggestions
 */

import { checkFilledInput } from './checkInputGroups';

const initializedInputs = new WeakSet();

/**
 * Load Google Maps API script dynamically
 * @param {string} apiKey - Google Maps API key
 * @returns {Promise} Resolves when API is loaded
 */
function loadGoogleMapsAPI(apiKey) {
    return new Promise((resolve, reject) => {
        // Check if already loaded
        if (window.google && window.google.maps && window.google.maps.places) {
            resolve();
            return;
        }

        // Check if script is already being loaded
        if (window.googleMapsAPILoading) {
            window.googleMapsAPILoading.then(resolve).catch(reject);
            return;
        }

        window.googleMapsAPILoading = new Promise((res, rej) => {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.defer = true;

            script.onload = () => {
                res();
            };

            script.onerror = () => {
                rej(new Error('Failed to load Google Maps API'));
            };

            document.head.appendChild(script);
        });

        window.googleMapsAPILoading.then(resolve).catch(reject);
    });
}

/**
 * Create autocomplete dropdown element
 * @param {HTMLElement} inputElement - Input element to attach dropdown to
 * @returns {HTMLElement} Dropdown element
 */
function createCustomDropdown(inputElement) {
    const dropdown = document.createElement('div');
    dropdown.className = 'google-address-autocomplete-dropdown';
    dropdown.style.cssText = `
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ccc;
        border-top: none;
        border-radius: 0 0 4px 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        max-height: 300px;
        overflow-y: auto;
        z-index: 1000;
        display: none;
    `;

    // Find the appropriate parent for positioning
    let parent = inputElement.closest('.infieldInputGroup') ||
                 inputElement.closest('.form-group') ||
                 inputElement.closest('[class*="input"]') ||
                 inputElement.parentElement;


    if (parent) {
        const computedStyle = window.getComputedStyle(parent);
        const position = computedStyle.position;

        // Only set position: relative if parent isn't already positioned
        if (position === 'static' || position === '' || position === 'inherit') {
            parent.style.position = 'relative';
        }

        parent.appendChild(dropdown);
    } else {
        document.body.appendChild(dropdown);
    }

    return dropdown;
}

/**
 * Extract address components from Google Geocoding result
 */
function extractAddressComponents(result) {
    const components = {
        street: '',
        streetNumber: '',
        postalCode: '',
        city: '',
        country: ''
    };

    if (!result || !result.address_components) {
        return components;
    }

    result.address_components.forEach((component) => {
        const types = component.types;

        if (types.includes('route')) {
            components.street = component.long_name;
        }
        if (types.includes('street_number')) {
            components.streetNumber = component.long_name;
        }
        if (types.includes('postal_code')) {
            components.postalCode = component.long_name;
        }
        if (types.includes('locality')) {
            components.city = component.long_name;
        }
        if (types.includes('country')) {
            components.country = component.short_name;
        }
    });

    return components;
}

/**
 * Fill address form fields with extracted components
 */
function fillAddressFields(fields, components) {

    if (fields.streetInput && components.street) {
        fields.streetInput.value = components.street;
        checkFilledInput(fields.streetInput);
        fields.streetInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (fields.streetNumberInput && components.streetNumber) {
        fields.streetNumberInput.value = components.streetNumber;
        checkFilledInput(fields.streetNumberInput);
        fields.streetNumberInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (fields.postalCodeInput && components.postalCode) {
        fields.postalCodeInput.value = components.postalCode;
        checkFilledInput(fields.postalCodeInput);
        fields.postalCodeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (fields.cityInput && components.city) {
        fields.cityInput.value = components.city;
        checkFilledInput(fields.cityInput);
        fields.cityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (fields.countrySelect && components.country) {
        // Try to find matching country option
        const options = fields.countrySelect.querySelectorAll('option');
        for (const option of options) {
            if (option.value.toUpperCase() === components.country.toUpperCase()) {
                fields.countrySelect.value = option.value;
                fields.countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
                break;
            }
        }
    }
}

/**
 * Initialize address autocomplete for order form fields
 * @param {Object} options - Configuration options
 */
export function initAddressAutocomplete(options = {}) {
    const defaultOptions = {
        streetSelector: '#input_user_street',
        streetNoSelector: '#input_user_street_no',
        postCodeSelector: '#input_post_code',
        citySelector: '#input_city',
        countrySelector: '#input_country'
    };

    const config = { ...defaultOptions, ...options };

    // Find the input elements
    const streetInput = document.querySelector(config.streetSelector);
    const streetNoInput = document.querySelector(config.streetNoSelector);
    const postCodeInput = document.querySelector(config.postCodeSelector);
    const cityInput = document.querySelector(config.citySelector);
    const countrySelect = document.querySelector(config.countrySelector);

    // Check if required fields exist
    if (!streetInput) {
        return;
    }

    // Check if already initialized
    if (initializedInputs.has(streetInput)) {
        return;
    }

    // Mark as initialized
    initializedInputs.add(streetInput);

    // Get API key from global config
    const apiKey = window.GLOBALS?.googleMapsApiKey;

    if (!apiKey) {
        console.warn('[orderAddressAutocomplete] Google Maps API key not found in GLOBALS');
        return;
    }


    // Initialize Google Address Autocomplete
    initGoogleAddressAutocompleteForOrder({
        apiKey: apiKey,
        streetInput: streetInput,
        streetNumberInput: streetNoInput,
        postalCodeInput: postCodeInput,
        cityInput: cityInput,
        countrySelect: countrySelect
    });
}

/**
 * Initialize address autocomplete for shipping fields
 */
export function initShippingAddressAutocomplete() {

    initAddressAutocomplete({
        streetSelector: '#input_user_street_shipping',
        streetNoSelector: '#input_user_street_no_shipping',
        postCodeSelector: '#input_post_code_shipping',
        citySelector: '#input_city_shipping',
        countrySelector: '#input_shop_country_shipping'
    });
}

/**
 * Initialize Google Address Autocomplete
 */
async function initGoogleAddressAutocompleteForOrder(config) {
    const {
        apiKey,
        streetInput,
        streetNumberInput,
        postalCodeInput,
        cityInput,
        countrySelect
    } = config;

    if (!streetInput) {
        console.error('[orderAddressAutocomplete] Street input is required');
        return;
    }

    try {
        // Load Google Maps API
        await loadGoogleMapsAPI(apiKey);

        // Create custom dropdown
        const dropdown = createCustomDropdown(streetInput);

        let currentSuggestions = [];
        let debounceTimer;

        // Input event handler
        const handleInput = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
                const query = streetInput.value.trim();

                if (query.length < 3) {
                    dropdown.style.display = 'none';
                    return;
                }

                try {
                    const autocompleteService = new google.maps.places.AutocompleteService();

                    autocompleteService.getPlacePredictions(
                        {
                            input: query,
                            types: ['address'],
                            componentRestrictions: { country: ['de', 'at', 'ch', 'nl', 'be', 'lu', 'fr', 'it', 'es', 'pt', 'pl', 'cz', 'sk', 'hu', 'si', 'hr'] }
                        },
                        (predictions, status) => {
                            if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
                                dropdown.style.display = 'none';
                                return;
                            }

                            currentSuggestions = predictions;
                            displaySuggestions(predictions, dropdown, streetInput);
                        }
                    );
                } catch (error) {
                    console.error('[orderAddressAutocomplete] Error fetching predictions:', error);
                }
            }, 300);
        };

        // Keydown event handler
        const handleKeydown = (e) => {
            if (dropdown.style.display === 'none') return;

            const items = dropdown.querySelectorAll('.google-address-autocomplete-item');
            const activeItem = dropdown.querySelector('.google-address-autocomplete-item.active');
            let currentIndex = activeItem ? Array.from(items).indexOf(activeItem) : -1;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    currentIndex = Math.min(currentIndex + 1, items.length - 1);
                    updateActiveItem(items, currentIndex);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    currentIndex = Math.max(currentIndex - 1, 0);
                    updateActiveItem(items, currentIndex);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (currentIndex >= 0 && currentSuggestions[currentIndex]) {
                        selectPrediction(currentSuggestions[currentIndex]);
                    }
                    break;
                case 'Escape':
                    dropdown.style.display = 'none';
                    break;
            }
        };

        // Select a prediction
        const selectPrediction = async (prediction) => {
            dropdown.style.display = 'none';

            const geocoder = new google.maps.Geocoder();

            geocoder.geocode({ placeId: prediction.place_id }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const components = extractAddressComponents(results[0]);

                    fillAddressFields({
                        streetInput,
                        streetNumberInput,
                        postalCodeInput,
                        cityInput,
                        countrySelect
                    }, components);
                } else {
                    console.error('[orderAddressAutocomplete] Geocoding failed:', status);
                }
            });
        };

        // Display suggestions in dropdown
        const displaySuggestions = (predictions, dropdown, inputElement) => {
            dropdown.innerHTML = '';

            predictions.forEach((prediction, index) => {
                const item = document.createElement('div');
                item.className = 'google-address-autocomplete-item';
                item.textContent = prediction.description;
                item.dataset.index = index;

                item.addEventListener('click', () => {
                    selectPrediction(prediction);
                });

                item.addEventListener('mouseenter', () => {
                    item.classList.add('active');
                });

                item.addEventListener('mouseleave', () => {
                    item.classList.remove('active');
                });

                dropdown.appendChild(item);
            });

            dropdown.style.display = 'block';
        };

        // Update active item styling
        const updateActiveItem = (items, activeIndex) => {
            items.forEach((item, index) => {
                if (index === activeIndex) {
                    item.classList.add('active');
                    item.scrollIntoView({ block: 'nearest' });
                } else {
                    item.classList.remove('active');
                }
            });
        };

        // Attach event listeners
        streetInput.addEventListener('input', handleInput);
        streetInput.addEventListener('keydown', handleKeydown);

        // Close dropdown on outside click
        document.addEventListener('click', (e) => {
            if (!dropdown.contains(e.target) && e.target !== streetInput) {
                dropdown.style.display = 'none';
            }
        });
    } catch (error) {
        console.error('[orderAddressAutocomplete] Error initializing address autocomplete:', error);
    }
}


